import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import * as moment from "moment";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { QueryParams } from "../../../core/http/query-params.type";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { FORMAT } from "../../../shared/constants/formats";
import { VALUE } from "../../../shared/constants/values";
import { MomentPipe } from "../../../shared/pipes/moment/moment.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { isBetweenValidator } from "../../../shared/validators/is-between.validator";
import { CompanyService } from "../companies/company.service";
import { MaintenanceProviderService } from "../maintenance-provider/maintenance-provider.service";
import { PartnerService } from "../partners/partner.service";
import { getQuoteSearchFilterOptions, quoteTypes } from "./quote";
import { QuoteService } from "./quote.service";
import { Quote } from "./quote.type";

@Injectable({
  providedIn: "root",
})
export class QuoteTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private quoteService: QuoteService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private maintenanceProviderService: MaintenanceProviderService,
    private dataTableService: DataTableService<Quote>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getQuotesTableConfig = (config?: DataTableConfig): DataTable<Quote> => {
    const { hidden, shown, actionColumn, inlineEdit, entities } = config || {};

    const columns: DataTableColumn<Quote>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (quote) => `/quotes/${quote.id}`,
          },
          inlineEdit: {
            control: "input",
            disabled: (quote) => inlineEdit?.disabled?.(quote),
            validators: () => [Validators.minLength(2), Validators.maxLength(255)],
          },
        },
        {
          prop: "number",
          name: "Quote #",
        },
        {
          prop: "company.partner.name",
          name: "Partner",
          hidden: !this.currentUser.isGlobal(),
          link: {
            path: (quote) => `/partners/${quote.company.partnerId}`,
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany(),
          link: {
            path: (quote) => `/companies/${quote.companyId}`,
          },
        },
        {
          prop: "contract.number",
          name: "Contract #",
          hidden: this.currentUser.isCompanyOrViewAsCompany(),
          link: {
            path: (quote) => `/contracts/${quote.contractId}`,
          },
          render: (quote) => quote.contract?.numberVersion,
          exportCsv: {
            browserColumnKey: "contract.numberVersion",
            csvMapperKey: "contract.numberVersion",
          },
        },
        {
          prop: "startDate",
          name: "Start Date",
          inlineEdit: {
            control: "date",
            validators: (quote) => [isBetweenValidator(VALUE.DEFAULT_MIN_DATE, quote.endDate, "[]")],
            date: {
              min: () => moment(VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (quote) => moment(quote.endDate).toDate(),
            },
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "endDate",
          name: "End Date",
          inlineEdit: {
            control: "date",
            validators: (quote) => [isBetweenValidator(quote.startDate, VALUE.DEFAULT_MAX_DATE, "[]")],
            disabled: (quote) => inlineEdit?.disabled?.(quote),
            date: {
              min: (quote) => moment(quote.startDate).toDate(),
              max: () => moment(VALUE.DEFAULT_MAX_DATE).toDate(),
            },
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "type",
          name: "Type",
          render: (quote) => quoteTypes.find((type) => type.value === quote.type)?.label,
          inlineEdit: {
            disabled: (quote) => inlineEdit?.disabled?.(quote),
            control: "select",
            select: {
              options: [...quoteTypes],
            },
          },
        },
        {
          prop: "maintenanceProvider.name",
          name: "Maintenance Provider",
          width: 200,
          hidden: this.currentUser.isCompanyOrViewAsCompany(),
          inlineEdit: {
            disabled: (quote) => inlineEdit?.disabled?.(quote),
            control: "search",
            search: {
              columnToUpdate: "maintenanceProviderId",
              propToUpdate: "maintenanceProvider",
              propToDisplay: "name",
              fetchDefaultData: true,
              service: (formValue: string) =>
                this.maintenanceProviderService.getMaintenanceProviders({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                  },
                }),
            },
          },
        },
      ] as DataTableColumn<Quote>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (quote) =>
            col?.inlineEdit?.disabled?.(quote) || !this.canEdit(this.currentUser, quote) || !!quote.contractId,
        },
      }));

    return {
      id: "quotes",
      entityName: "Quote",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "quotes",
          data: {
            browser: (queryParams) =>
              this.quoteService.getQuotes({
                ...queryParams,
                where: {
                  companyId: entities?.company?.id,
                  ...removePropsWithKeys<Quote>(queryParams.where || {}, ["partner.name", "company.name"]),
                },
                // "contract" object need to have "number" and "version" in order
                // to populate the csv properly
                select: { ...queryParams.select, contract: { number: true, version: true } },
              }),
            email: (queryParams) =>
              this.quoteService.getQuotesQueue({
                ...queryParams,
                where: {
                  companyId: entities?.company?.id,
                  ...removePropsWithKeys<Quote>(queryParams.where || {}, ["partner.name", "company.name"]),
                },
                // "contract" object need to have "number" and "version" in order
                // to populate the csv properly
                select: { ...queryParams.select, contract: { number: true, version: true } },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewQuote(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageQuote,
              hidden: (quote) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteQuote,
              hidden: (quote) => actionColumn?.delete?.hidden || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getQuoteSearchFilterOptions(this.currentUser, {
            hidden,
            shown,
            partnerService: this.partnerService,
            companyService: this.companyService,
          }),
        },
      ],
    };
  };

  /** Populate table */
  getQuotes = (event: GetDataEvent<Quote>): void => {
    const { onError, onResult, queryParams } = event;
    const params: QueryParams<Quote> = {
      ...queryParams,
      relations: { maintenanceProvider: true, contract: true, company: { partner: true } },
      where: {
        ...removePropsWithKeys<Quote>(queryParams.where, ["partner.name", "company.name"]),
      },
    };

    this.quoteService.getQuotes(params).subscribe(
      (data) => {
        onResult(data);
      },
      (error) => {
        onError(error);
      },
    );
  };

  /** Update table */
  update = (event: UpdateDataEvent<Quote>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      // If startDate is updated then we have to pass its endDate too
      // else if endDate is updated then we have to pass its startDate too
      if (updatedValues.startDate) {
        updatedValues.endDate = row.endDate;
      } else if (updatedValues.endDate) {
        updatedValues.startDate = row.startDate;
      }
      this.quoteService.updateQuote(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewQuote = (quote: Quote, config?: DataTableConfig): void => {
    const columns = this.getQuotesTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(quote, col, get(quote, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageQuote = (quote: Quote): void => {
    this.router.navigate([`/quotes/${quote.id}`]);
  };

  private deleteQuote = (quote: Quote, table: DataTableComponent<Quote>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "quote",
        data: quote,
        url: this.quoteService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  /** Check if can edit a quote (on the table) */
  private canEdit = (currentUser: UserInfo, quote: Quote): boolean => {
    return (
      (currentUser.isGlobal() && currentUser.hasRight(["admin", "manager"])) ||
      (!currentUser.isGlobal() &&
        currentUser.hasRight(["admin", "manager"]) &&
        !quote.maintenanceProvider?.isChannelWorks)
    );
  };

  //#endregion
}
